import { AuditLogResponseData } from '../../hooks/use-audit-log';
import {
  ToleranceMode,
  WaterAdjustBy,
} from '../../constants/Configuration';
import { useStores } from '../../hooks/use-stores';
import { isObjectLike } from 'lodash';

export const PROPERTY_MAP: Record<string, null | any> = {
  autoTuning: { label: 'BatchGo AI' },
  enableJogAiLearning: null, // null = do not render
  toleranceMode: {
    value: Object.fromEntries(
      Object.entries(ToleranceMode).map(([key, value]) => [value, key]),
    ),
  },
  saveWaterAdjustBy: {
    value: Object.fromEntries(
      Object.entries(WaterAdjustBy).map(([key, value]) => [value, key]),
    ),
  },
  defaultDischargeSequenceId: {
    valueFromStore: 'sequences',
    keys: ['id', 'name'],
  },
  slumpTableId: {
    valueFromStore: 'slumpTables',
    keys: ['value', 'name'],
  },
  discharges: {
    label: 'Scale',
  },
};

export const getValueFromStore = (
  settingsStore: ReturnType<typeof useStores>['settingsStore'],
  prop: string,
  keys: [string, string] = ['id', 'name'],
) => {
  const [keyField, valueField] = keys;

  // @ts-ignore
  return Object.fromEntries((settingsStore[prop] ?? []).map((item) => [
    item[keyField],
    item[valueField],
  ]));
};

export const mapPropertyValue = (
  propertyKey: string,
  value: unknown,
  settingsStore: ReturnType<typeof useStores>['settingsStore'],
) => {
  const mapped = PROPERTY_MAP[propertyKey];

  if (value == null || !mapped) {
    return value;
  }

  if (mapped.valueFromStore) {
    const valueFromStore = getValueFromStore(
      settingsStore,
      mapped.valueFromStore,
      mapped.keys,
    );

    const storeValue = valueFromStore?.[value as keyof typeof valueFromStore];

    if (storeValue != null) {
      return storeValue;
    }
  }

  const mappedValue = mapped.value?.[value as keyof typeof mapped.value];

  return mappedValue ?? value;
};

export const modifyItem = (
  item: AuditLogResponseData['items'][0],
): void => {
  if (item.actionType !== 'Update' || !item.details?.from) {
    return;
  }

  const { from, to } = item.details;

  const changes: {
    from: Record<string, unknown>;
    to: Record<string, unknown>;
  } = {
    from: {},
    to: {},
  };

  const hasChanges = (
    change: ReturnType<typeof getObjectChanges>,
  ): boolean =>
    Object.keys(change.from).length > 0 &&
    Object.keys(change.to).length > 0;

  const getMappedKey = (key: string, index?: number): string => {
    const mapped = PROPERTY_MAP[key]?.label || key;
    return index === undefined ? mapped : `${mapped} ${index + 1}`;
  };

  const addChanges = (
    key: string,
    change: ReturnType<typeof getObjectChanges>,
    index?: number,
  ): void => {
    if (!hasChanges(change)) {
      return;
    }

    const mappedKey = getMappedKey(key, index);

    changes.from[mappedKey] = change.from;
    changes.to[mappedKey] = change.to;

    delete from[key];
    delete to[key];
  };

  Object.keys(from).forEach((key) => {
    const valueFrom = from[key];
    const valueTo = to?.[key];

    if (JSON.stringify(valueFrom) === JSON.stringify(valueTo)) {
      delete from[key];
      delete to[key];
      return;
    }

    if (Array.isArray(valueFrom) && Array.isArray(valueTo)) {
      if (
        valueFrom.length !== valueTo.length ||
        !valueFrom.every((prop, index) =>
          isObjectLike(prop) &&
          !Array.isArray(prop) &&
          isObjectLike(valueTo[index]) &&
          !Array.isArray(valueTo[index]))
      ) {
        return;
      }
      valueFrom.forEach((prop, index) => {
        const target = valueTo[index];

        if (
          prop === null ||
          typeof prop !== 'object' ||
          Array.isArray(prop) ||
          target === null ||
          typeof target !== 'object' ||
          Array.isArray(target)
        ) {
          return;
        }

        addChanges(key, getObjectChanges(prop, target), index);
      });

      return;
    }

    if (isObjectLike(valueFrom) && isObjectLike(valueTo)) {
      addChanges(
        key,
        getObjectChanges(
          valueFrom as Record<string, any>,
          valueTo as Record<string, any>,
        ),
      );
    }
  });

  Object.assign(from, changes.from);
  Object.assign(to, changes.to);
};

export const getObjectChanges = <T extends Record<string, any>>(
  from: T,
  to: T,
): any => {
  const fromChanges: Partial<T> = {};
  const toChanges: Partial<T> = {};

  const keys = new Set([
    ...Object.keys(from),
    ...Object.keys(to),
  ]) as Set<keyof T>;

  // @ts-ignore
  for (const key of keys) {
    if (!Object.is(from[key], to[key])) {
      // @ts-ignore
      fromChanges[key] = from[key];
      // @ts-ignore
      toChanges[key] = to[key];
    }
  }

  return {
    from: fromChanges,
    to: toChanges,
  };
};
